import { ChangeDetectionStrategy, Component, inject, signal, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ReportRunState } from '../report-run-state';
import { logger } from '@fry-it/risr-core';

export interface CsvDownloadDialogData {
    outputName: string;
    reportRunState: ReportRunState;
}

@Component({
    selector: 'risr-features-reports-csv-download-dialog',
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButtonModule,
        MatProgressBarModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <h2 mat-dialog-title i18n>Download CSV</h2>
        <mat-dialog-content>
            @switch (state()) {
                @case ('generating') {
                    <p i18n>Generating CSV file...</p>
                    <mat-progress-bar mode="determinate" [value]="progress()"></mat-progress-bar>
                }
                @case ('downloading') {
                    <p i18n>Download starting...</p>
                    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                }
                @case ('error') {
                    <p style="color: var(--mat-sys-error)" i18n>An error occurred while generating the CSV file.</p>
                    <p>{{ errorMessage() }}</p>
                }
                @case ('done') {
                    <p i18n>Download complete.</p>
                }
            }
        </mat-dialog-content>
        <mat-dialog-actions>
            @if (state() === 'error' || state() === 'done') {
                <button mat-button (click)="dialogRef.close()">
                    <ng-container i18n>Close</ng-container>
                </button>
            }
            @if (state() === 'generating' || state() === 'downloading') {
                <button mat-button (click)="cancel()">
                    <ng-container i18n>Cancel</ng-container>
                </button>
            }
        </mat-dialog-actions>
    `,
})
export class CsvDownloadDialog implements OnInit, OnDestroy {
    readonly dialogRef = inject(MatDialogRef<CsvDownloadDialog>);
    private readonly data = inject<CsvDownloadDialogData>(MAT_DIALOG_DATA);

    readonly state = signal<'generating' | 'downloading' | 'done' | 'error'>('generating');
    readonly progress = signal(0);
    readonly errorMessage = signal('');

    private static readonly POLL_MIN_MS = 500;
    private static readonly POLL_MAX_MS = 5000;
    private static readonly POLL_STEP_MS = 500;

    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollInterval = CsvDownloadDialog.POLL_MIN_MS;
    private lastProgress = 0;
    private cancelled = false;
    private taskId: string | undefined;

    ngOnInit() {
        this.startGeneration();
    }

    ngOnDestroy() {
        this.cancelled = true;
        this.clearPoll();
    }

    async cancel() {
        this.cancelled = true;
        if (this.taskId) {
            try {
                await this.data.reportRunState.abortTask(this.taskId);
            } catch (err) {
                logger.error('Failed to abort task', err instanceof Error ? err : new Error(String(err)));
            }
        }
        this.clearPoll();
        this.dialogRef.close();
    }

    private async startGeneration() {
        try {
            const res = await this.data.reportRunState.generateCSV(this.data.outputName);
            if (this.cancelled) return;
            const taskId = res.celeryTaskId;
            this.taskId = taskId;
            this.pollTaskStatus(taskId);
        } catch (e: unknown) {
            this.errorMessage.set(e instanceof Error ? e.message : String(e));
            this.state.set('error');
        }
    }

    private pollTaskStatus(taskId: string) {
        const poll = async () => {
            if (this.cancelled) return;
            try {
                const status = await this.data.reportRunState.getTaskStatus(taskId);
                if (this.cancelled) return;

                const rateComplete = status?.meta?.rate_complete ?? 0;
                this.progress.set(rateComplete);

                const taskState = status?.state;

                if (taskState === 'success') {
                    this.state.set('downloading');
                    await this.downloadResult(taskId);
                    return;
                }

                if (taskState === 'failure') {
                    const errors = status?.meta?.errors ?? [];
                    this.errorMessage.set(errors.length ? errors.join(', ') : $localize`Task failed`);
                    this.state.set('error');
                    return;
                }

                if (rateComplete > this.lastProgress) {
                    this.pollInterval = CsvDownloadDialog.POLL_MIN_MS;
                } else {
                    this.pollInterval = Math.min(this.pollInterval + CsvDownloadDialog.POLL_STEP_MS, CsvDownloadDialog.POLL_MAX_MS);
                }
                this.lastProgress = rateComplete;
                this.pollTimer = setTimeout(poll, this.pollInterval);
            } catch (e: unknown) {
                if (this.cancelled) return;
                this.errorMessage.set(e instanceof Error ? e.message : String(e));
                this.state.set('error');
            }
        };
        poll();
    }

    private async downloadResult(taskId: string) {
        try {
            const result = await this.data.reportRunState.getResultUrl(taskId);
            if (this.cancelled) return;

            const url = result?.url;
            if (!url) {
                this.errorMessage.set($localize`No download URL returned`);
                this.state.set('error');
                return;
            }

            const link = document.createElement('a');
            link.href = url;
            link.download = '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.state.set('done');
            setTimeout(() => {
                if (!this.cancelled) {
                    this.dialogRef.close();
                }
            }, 1500);
        } catch (e: unknown) {
            if (this.cancelled) return;
            this.errorMessage.set(e instanceof Error ? e.message : String(e));
            this.state.set('error');
        }
    }

    private clearPoll() {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }
}
